import { TemplateTypeDescriptionDetailed } from "./classification/ModelTypeEnabledDocTypeTooltips";
import { ExpandingTooltip } from "./ExpandingTooltip";

export {
  ClassificationPanel as TTClassificationPanel,
  TemplateName as TTTemplateName,
  DocumentStartPatterns as TTDocumentStartPatterns,
  DocumentEndPatterns as TTDocumentEndPatterns,
  ViewOcrStart as TTViewOcrStart,
  ViewOcrEnd as TTViewOcrEnd,
};

// Item 1
const ClassificationPanel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Configure the rules to configure a document as a Specific, General
        Classification or General Format template.
      </p>
    );
  return (
    <>
      <p>
        In the <strong>Classification</strong> panel, configure the rules used
        to classify documents as either a Specific or a General template. By
        default, the document will be classified as a Specific template for the
        selected Document Type (e.g., a specific supplier’s Invoice template, or
        a specific customer’s Purchase Order template). Select the type of
        template you want to configure from the <strong>Template Type</strong>{" "}
        dropdown.
      </p>
      {TemplateTypeDescriptionDetailed()}
    </>
  );
};

// Item 2
const TemplateName: ExpandingTooltip = (short) => {
  if (short) {
    return (
      <p>Enter a unique template name for a new template that you configure.</p>
    );
  } else {
    return (
      <p>
        Enter a unique name for a template in the <strong>Template Name</strong>{" "}
        box. Use a consistent naming convention across all templates (e.g.,{" "}
        {"<supplier name>_<supplier number>"} for specific invoice templates).
        Click the <strong>Confirm</strong> button to save the template name. If
        a template with the name you enter already exists, a warning message
        will be displayed to prevent you from inadvertently overwriting an
        existing template.
      </p>
    );
  }
};

// Item 21
const DocumentStartPatterns: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Click <strong>Add Rule</strong> to add Document Start Patterns for a
        template. These are used to classify a document as this template.
      </p>
    );
  return (
    <>
      <p>
        <strong>Document Start Patterns</strong> are critically important for
        optimising document classification.
      </p>
      <ul>
        <li>
          All rules must fire on a page to classify the page as the <u>first</u>{" "}
          page of a template.{" "}
        </li>
        <li>
          Rules are regexes but are often just the text string as it appears on
          the document.{" "}
        </li>
        <li>Must escape out special regex characters. </li>
        <li>Never use variable data (e.g., an invoice number). </li>
        <li>
          Always test against multiple samples (to identify variants and common
          OCR issues).{" "}
        </li>
        <li>
          Best practice rules for Specific templates. At least one rule should
          be:{" "}
        </li>
        <ul>
          <li>
            Unique to the supplier/customer (e.g., the name of the
            supplier/customer that sent the document).{" "}
          </li>
          <li>
            Unique to the first page (e.g., Page 1 ). Include a space after the
            1 to exclude Page 10, 11 etc. If there is nothing unique to Page 1,
            use a Document End Pattern.{" "}
          </li>
          <li>
            Unique to the Document Type (e.g., Tax Invoice or Credit Note),
            exactly as the wording appears on this supplier’s/customer’s
            documents.{" "}
          </li>
        </ul>
      </ul>
    </>
  );
};

// Item 22
const DocumentEndPatterns: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Click <strong>Add Rule</strong> to add Document End Patterns for a
        template.
      </p>
    );
  return (
    <>
      <p>
        Document End Patterns are only used when there is nothing unique on the
        first page of a document to differentiate the first page from other
        pages.
      </p>
      <ul>
        <li>
          Used if there is nothing unique on the first page relative to any
          other page.
        </li>
        <li>
          In this scenario, the document will be treated as a single document
          until the Document End Patterns all fire, and Accelerator will then
          separate the document at the page where the End Patterns fire.{" "}
        </li>
        <li>
          Often, only a single End Pattern is required (e.g., banking or payee
          details).{" "}
        </li>
        <li>Must escape out special regex characters. </li>
      </ul>
    </>
  );
};

// Item 25
const ViewOcrStart: ExpandingTooltip = (short) => {
  if (short)
    return <p>Click to view/hide the OCR for the selected document.</p>;
  return (
    <p>
      When the View OCR button is toggled to On, the OCR extracted by Transact
      will be displayed. For multi-page documents, pages are separated and
      labelled (Page 1, Page 2 etc.). If a Document Start Pattern rule has
      fired, the data extracted will be highlighted and colour coded in the OCR
      area. Only configure Document Start Pattern rules to locate non-variable
      data that always appears on <u>Page 1</u> of the document.
    </p>
  );
};

// Item 26
const ViewOcrEnd: ExpandingTooltip = (short) => {
  if (short)
    return <p>Click to view/hide the OCR for the selected document.</p>;
  return (
    <p>
      When the View OCR button is toggled to On, the OCR extracted by Transact
      will be displayed. For multi-page documents, pages are separated and
      labelled (Page 1, Page 2 etc.). If a Document End Pattern rule has fired,
      the data extracted will be highlighted and colour coded in the OCR area.
      Only configure Document End Pattern rules to locate non-variable data that
      always appears on the <u>last page</u> of the document. It is fine for
      Document Start and Document End patterns to fire on the same page if the
      document is a single page.
    </p>
  );
};
